import React, { useEffect, useMemo, useRef, useState } from "react";

/* ══════════════════════════════════════════════════════════════════════
   PANORIX — THE PERCEPTION FIELD (FLAGSHIP)
   An active cognitive gaze across a capital project. Six dimensional
   strata, populated data points, scanning attention cone, lock-on
   detail panels, cross-stratum correlation events.
   Pure SVG + CSS keyframes + React state (no Three.js)
   SCAN · CONNECT · SEE
   ══════════════════════════════════════════════════════════════════════ */

const W = 680, H = 580;
const CX = W / 2, CY = H / 2 + 6;

/* ─── Seeded RNG for stable placement ─── */
function makeRng(seed) {
    let s = seed >>> 0;
    return () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 4294967296;
    };
}

/* ─── Strata definitions ─── */
const STRATA = [
    { id: "sched",    label: "SCHEDULE",   sub: "847 ACTIVITIES",   r: 105, markers: 16, glyph: "diamond", labelAngle: -100 },
    { id: "cost",     label: "COST",       sub: "₹4,247 CR",       r: 145, markers: 16, glyph: "bar",     labelAngle: -55 },
    { id: "quality",  label: "QUALITY",    sub: "14,200 ITP",      r: 183, markers: 18, glyph: "square",  labelAngle: 160 },
    { id: "hse",      label: "HSE",        sub: "0 LTI",           r: 218, markers: 16, glyph: "dot",     labelAngle: 120 },
    { id: "progress", label: "PROGRESS",   sub: "62% VPR",         r: 250, markers: 18, glyph: "wedge",   labelAngle: -140 },
    { id: "supplier", label: "SUPPLIERS",  sub: "247 ACTIVE",      r: 280, markers: 18, glyph: "cross",   labelAngle: 35 },
];

/* ─── Finding pool (realistic capital-project locks) ─── */
const FINDINGS = {
    sched: [
        { sev: "attention", finding: "Unit-3 CP slipped 7 days",             link: "Linked: PO-2847 late · NCR-0247" },
        { sev: "attention", finding: "Foundation pour delayed",              link: "IMD rainfall 87% above mean"    },
        { sev: "attention", finding: "Mech completion drift +14d",           link: "CPI 0.92 trending"              },
        { sev: "attention", finding: "Commissioning window compressed",      link: "Vendor witness test pending"    },
    ],
    cost: [
        { sev: "attention", finding: "EAC trending +2.4%",                   link: "Steel escalation Q4"            },
        { sev: "attention", finding: "Unit-2 variance +₹12 Cr",              link: "Rework scope"                   },
        { sev: "attention", finding: "Unplanned capex ₹8.7 Cr",              link: "Regulatory change"              },
        { sev: "critical",  finding: "EAC breach: +5.2% ceiling",            link: "Contract LD exposure"           },
    ],
    quality: [
        { sev: "attention", finding: "NCR spike: 14 this week",              link: "Subcontractor 14-B"             },
        { sev: "attention", finding: "FTR dropping: 94% → 89%",              link: "Welding repeats climbing"       },
        { sev: "attention", finding: "ITP backlog: 247 open",                link: "Inspector capacity"             },
        { sev: "critical",  finding: "NCR escalation: weld defect",          link: "Scope: 247m rework"             },
    ],
    hse: [
        { sev: "attention", finding: "Near-miss cluster: crane ops",         link: "Same crew · 3 events"           },
        { sev: "attention", finding: "Permit compliance drift",              link: "Hot-work zone overlap"          },
        { sev: "attention", finding: "PPE audit: 87% → 81%",                 link: "New subcontractor"              },
        { sev: "critical",  finding: "STOP-WORK TRIGGER · Unit-2",           link: "Safety interlock fault"         },
    ],
    progress: [
        { sev: "attention", finding: "VPR deviation: Area-7 piping",         link: "Claimed 82% · Scan 74%"         },
        { sev: "attention", finding: "Drone scan anomaly",                   link: "V-301 offset from model"        },
        { sev: "attention", finding: "Earned value dropping",                link: "Material late stream"           },
        { sev: "attention", finding: "Planned vs actual widening",           link: "Unit-2 commissioning"           },
    ],
    supplier: [
        { sev: "attention", finding: "Vendor Gamma critical",                link: "3 late deliveries · stress"     },
        { sev: "attention", finding: "Sole-source risk: Beta",               link: "Force majeure declared"         },
        { sev: "attention", finding: "Logistics port delay",                 link: "Mumbai-3 dwell ↑"               },
        { sev: "critical",  finding: "Vendor insolvency signal",             link: "Gamma · critical path"          },
    ],
};

/* ─── Cross-stratum correlations (rarer, more impactful) ─── */
const CROSS_FINDINGS = [
    { a: "sched", b: "supplier", finding: "Schedule slip → supplier root cause", sub: "Unit-3 delay ↔ Vendor Gamma late deliveries" },
    { a: "quality", b: "sched",  finding: "Quality backlog delaying milestone", sub: "247 open ITP ↔ Unit-2 mech completion" },
    { a: "cost", b: "sched",     finding: "Cost overrun driven by schedule",    sub: "EAC +2.4% ↔ Critical path extension" },
    { a: "hse", b: "progress",   finding: "Safety slowdown impacting scan",     link: "Stop-work ↔ Area-7 progress gap" },
];

/* ─── Generate data points (stable) ─── */
function buildDataPoints() {
    const rng = makeRng(20260131);
    const pts = [];
    let gid = 0;
    STRATA.forEach((s) => {
        const n = s.markers;
        // non-uniform: some clustering
        const clusterCenters = Array.from({ length: 3 }, () => rng() * Math.PI * 2);
        for (let i = 0; i < n; i++) {
            // 70% around cluster centers, 30% uniform
            let θ;
            if (rng() < 0.7) {
                const c = clusterCenters[Math.floor(rng() * clusterCenters.length)];
                θ = c + (rng() - 0.5) * 1.4;
            } else {
                θ = rng() * Math.PI * 2;
            }
            // severity state: 85% normal, 12% attention, 3% critical
            const r = rng();
            let sev = "normal";
            if (r > 0.97) sev = "critical";
            else if (r > 0.85) sev = "attention";

            pts.push({
                id: gid++,
                stratumId: s.id,
                r: s.r,
                θ,
                x: CX + s.r * Math.cos(θ),
                y: CY + s.r * Math.sin(θ),
                sev,
                glyph: s.glyph,
                breathePhase: rng() * 10,
            });
        }
    });
    return pts;
}

/* ══════════════════════════════════════════════════════════════════════
   COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function PanorixPerceptionField() {
    const [reduceMotion, setReduceMotion] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduceMotion(mq.matches);
        const fn = (e) => setReduceMotion(e.matches);
        mq.addEventListener("change", fn);
        return () => mq.removeEventListener("change", fn);
    }, []);

    const dataPoints = useMemo(buildDataPoints, []);
    const attnPoints = useMemo(() => dataPoints.filter((p) => p.sev !== "normal"), [dataPoints]);

    /* ─── Scan angle (degrees, 0 = east, increasing clockwise) ─── */
    const [scanAngle, setScanAngle] = useState(-90);
    const [lock, setLock] = useState(null);            // active lock event
    const scanPausedRef = useRef(false);
    const rafRef = useRef(null);
    const tRef = useRef(0);
    const lastCrossRef = useRef(0);
    const lockCountRef = useRef(0);

    /* ─── Animation loop (RAF) ─── */
    useEffect(() => {
        if (reduceMotion) {
            // Set a representative static lock
            const p = attnPoints[0];
            if (p) {
                const f = (FINDINGS[p.stratumId] || [])[0];
                setScanAngle((p.θ * 180 / Math.PI) - 90);
                setLock({ phase: "hold", points: [p], finding: f, cross: false });
            }
            return;
        }
        const step = (now) => {
            if (tRef.current === 0) tRef.current = now;
            const dt = (now - tRef.current) / 1000;
            tRef.current = now;
            if (!scanPausedRef.current) {
                setScanAngle((a) => (a + dt * 360 / 16) % 360); // 16s/rev
            }
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); tRef.current = 0; };
    }, [reduceMotion, attnPoints]);

    /* ─── Trigger locks when scan approaches attention points ─── */
    useEffect(() => {
        if (reduceMotion || lock) return;
        // convert scan angle to radians
        const scanRad = (scanAngle + 90) * Math.PI / 180;
        const threshold = 0.09; // angular proximity (~5°)
        const nearest = attnPoints.find((p) => {
            let diff = Math.abs(p.θ - scanRad);
            diff = Math.min(diff, 2 * Math.PI - diff);
            return diff < threshold;
        });
        if (!nearest) return;

        // decide cross-stratum vs single
        lockCountRef.current += 1;
        const timeSinceCross = performance.now() - lastCrossRef.current;
        const doCross = timeSinceCross > 42000 && Math.random() < 0.4;

        let lockPayload;
        if (doCross) {
            // find another attention point in a different stratum
            const other = attnPoints.find((p) =>
                p.stratumId !== nearest.stratumId && p.id !== nearest.id
            );
            if (other) {
                const cross = CROSS_FINDINGS.find((c) =>
                    (c.a === nearest.stratumId && c.b === other.stratumId) ||
                    (c.b === nearest.stratumId && c.a === other.stratumId)
                ) || CROSS_FINDINGS[0];
                lockPayload = { phase: "narrow", points: [nearest, other], finding: cross, cross: true };
                lastCrossRef.current = performance.now();
            }
        }
        if (!lockPayload) {
            const pool = FINDINGS[nearest.stratumId] || FINDINGS.sched;
            const finding = pool[lockCountRef.current % pool.length];
            lockPayload = { phase: "narrow", points: [nearest], finding, cross: false };
        }
        scanPausedRef.current = true;
        setLock(lockPayload);
    }, [scanAngle, reduceMotion, lock, attnPoints]);

    /* ─── Lock state machine: narrow → line → panel(hold) → release ─── */
    useEffect(() => {
        if (!lock || reduceMotion) return;
        let t;
        if (lock.phase === "narrow") {
            t = setTimeout(() => setLock({ ...lock, phase: "line" }), 320);
        } else if (lock.phase === "line") {
            t = setTimeout(() => setLock({ ...lock, phase: "hold" }), 420);
        } else if (lock.phase === "hold") {
            t = setTimeout(() => setLock({ ...lock, phase: "release" }), 1700);
        } else if (lock.phase === "release") {
            t = setTimeout(() => {
                setLock(null);
                scanPausedRef.current = false;
            }, 380);
        }
        return () => clearTimeout(t);
    }, [lock, reduceMotion]);

    /* ─── Scan cone geometry ─── */
    const coneAngle = lock && (lock.phase === "line" || lock.phase === "hold") ? 8 : 20;
    const coneLen = 300;
    const scanRad = (scanAngle + 90) * Math.PI / 180;
    const coneA = scanRad - (coneAngle * Math.PI / 180) / 2;
    const coneB = scanRad + (coneAngle * Math.PI / 180) / 2;
    const conePath = `M ${CX} ${CY}
        L ${CX + coneLen * Math.cos(coneA)} ${CY + coneLen * Math.sin(coneA)}
        A ${coneLen} ${coneLen} 0 0 1 ${CX + coneLen * Math.cos(coneB)} ${CY + coneLen * Math.sin(coneB)}
        Z`;

    return (
        <div className="dp-wrap" data-testid="dp-canvas" role="img"
             aria-label="Animated illustration: Panorix as an active cognitive perception system. At center is an attention node — the eye of Panorix. Six concentric measurement rings surround it: schedule, cost, quality, safety, progress, and supplier health. A scan cone rotates continuously. When it encounters an anomaly, the attention locks onto the data point and a detail panel shows the finding with cross-references. Occasionally Panorix connects correlations across multiple dimensions.">
            <svg viewBox={`0 0 ${W} ${H}`} className={`dp-svg ${reduceMotion ? "dp-reduced" : ""}`}
                 preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                <defs>
                    <pattern id="dp-grid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                        <path d="M 32 0 L 0 0 0 32" fill="none" className="dp-grid-line" />
                    </pattern>
                    <radialGradient id="dp-grid-fade" cx="50%" cy="50%" r="50%">
                        <stop offset="0%"  stopColor="#000" stopOpacity="1" />
                        <stop offset="70%" stopColor="#000" stopOpacity="0.55" />
                        <stop offset="100%" stopColor="#000" stopOpacity="0" />
                    </radialGradient>
                    <mask id="dp-grid-mask">
                        <rect width="100%" height="100%" fill="url(#dp-grid-fade)" />
                    </mask>
                    <radialGradient id="dp-cone-grad" cx="0%" cy="50%" r="100%">
                        <stop offset="0%"   stopColor="#C2D5EE" stopOpacity="0.45" />
                        <stop offset="45%"  stopColor="#5790C9" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#5790C9" stopOpacity="0.05" />
                    </radialGradient>
                    <radialGradient id="dp-eye-core">
                        <stop offset="0%"   stopColor="#E6EEF9" stopOpacity="1"  />
                        <stop offset="100%" stopColor="#C2D5EE" stopOpacity="0.75" />
                    </radialGradient>
                </defs>

                {/* ── LAYER 1: BACKGROUND GRID + RADIAL FADE ── */}
                <g mask="url(#dp-grid-mask)">
                    <rect x="0" y="26" width={W} height={H - 48} fill="url(#dp-grid)" />
                </g>

                {/* ── Perception heartbeat (slow radial pulse) ── */}
                {!reduceMotion && (
                    <circle cx={CX} cy={CY} r="40" className="dp-heartbeat" fill="none" />
                )}

                {/* ── LAYER 2: STRATA RINGS + LABELS ── */}
                {STRATA.map((s, i) => {
                    // Each stratum label at its own unique angle to avoid overlap
                    const labelRad = s.labelAngle * Math.PI / 180;
                    const labelR = s.r + 4;
                    const lx = CX + labelR * Math.cos(labelRad);
                    const ly = CY + labelR * Math.sin(labelRad);
                    const textAnchor = Math.abs(s.labelAngle) > 90 ? "end" : "start";
                    return (
                        <g key={s.id} className={`dp-stratum dp-stratum-${s.id}`}>
                            <circle cx={CX} cy={CY} r={s.r} className="dp-ring" />
                            {/* Label positioned at unique angle */}
                            <text x={lx} y={ly - 3} textAnchor={textAnchor} className="dp-label">{s.label}</text>
                            <text x={lx} y={ly + 9} textAnchor={textAnchor} className="dp-label-sub">{s.sub}</text>
                        </g>
                    );
                })}

                {/* ── LAYER 3: DATA POINTS ── */}
                <g className="dp-points">
                    {dataPoints.map((p) => (
                        <DataPoint key={p.id} p={p} />
                    ))}
                </g>

                {/* ── LAYER 5: SCAN CONE ── */}
                <path d={conePath} fill="url(#dp-cone-grad)" className="dp-cone"
                      style={{ opacity: lock && lock.phase === "release" ? 0.3 : 0.85 }} />

                {/* ── LAYER 6: LOCK EVENT RENDERING ── */}
                {lock && <LockRender lock={lock} />}

                {/* ── LAYER 4: ATTENTION NODE (rendered late so it's on top) ── */}
                <AttentionNode locked={!!lock} />

                {/* ── TOP OVERLAY ── */}
                <rect x="0" y="0" width={W} height="24" className="dp-topbar" />
                <text x="8" y="16" className="dp-top-txt">PROJECT · ₹4,247 CR · FEL-4</text>
                <text x={W / 2} y="16" textAnchor="middle" className="dp-top-txt">LIVE · 18 MODULES · 6 DIMENSIONS</text>
                <g transform={`translate(${W - 8}, 12)`}>
                    <text x="0" y="4" textAnchor="end" className="dp-top-txt">PANORIX COGNITIVE LAYER</text>
                    <path d="M-148,-4 L-152,1 L-148,1 L-149,6 L-144,-1 L-147,-1 Z" className="dp-spark dp-spark-static" />
                </g>
                <line x1="0" y1="25" x2={W} y2="25" className="dp-hair" />

                {/* ── BOTTOM OVERLAY ── */}
                <line x1="0" y1={H - 18} x2={W} y2={H - 18} className="dp-hair" />
                <text x="8" y={H - 4} className="dp-bot-label">SCAN</text>
                <text x={W / 2} y={H - 4} textAnchor="middle" className="dp-bot-label dp-bot-accent">CONNECT</text>
                <text x={W - 8} y={H - 4} textAnchor="end" className="dp-bot-label dp-bot-accent">SEE</text>
            </svg>

            <span className="sr-only">
                Panorix scans six dimensions of a capital project — schedule, cost, quality, safety, progress, and suppliers. A central attention node continuously scans, locks onto anomalies, and connects correlations across dimensions.
            </span>
        </div>
    );
}

/* ─── Data point glyph selection ─── */
function DataPoint({ p }) {
    const cls = `dp-pt dp-pt-${p.sev}`;
    const style = { animationDelay: `${p.breathePhase}s` };
    switch (p.glyph) {
        case "diamond":
            return <rect x={p.x - 4} y={p.y - 4} width="8" height="8" transform={`rotate(45 ${p.x} ${p.y})`} className={cls} style={style} />;
        case "bar":
            return <rect x={p.x - 5} y={p.y - 2} width="10" height="4" className={cls} style={style} />;
        case "square":
            return <rect x={p.x - 3.5} y={p.y - 3.5} width="7" height="7" className={cls} style={style} />;
        case "dot":
            return <circle cx={p.x} cy={p.y} r="2.5" className={cls} style={style} />;
        case "wedge":
            return <path d={`M ${p.x - 5} ${p.y + 3} L ${p.x + 5} ${p.y + 3} L ${p.x} ${p.y - 4} Z`} className={cls} style={style} />;
        case "cross":
            return (
                <g className={cls} style={style}>
                    <line x1={p.x - 4} y1={p.y} x2={p.x + 4} y2={p.y} />
                    <line x1={p.x} y1={p.y - 4} x2={p.x} y2={p.y + 4} />
                </g>
            );
        default:
            return <circle cx={p.x} cy={p.y} r="2.5" className={cls} style={style} />;
    }
}

/* ─── Attention node (the eye) ─── */
function AttentionNode({ locked }) {
    const spokes = Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2;
        return {
            x2: CX + 13 * Math.cos(a),
            y2: CY + 13 * Math.sin(a),
        };
    });
    return (
        <g className={`dp-eye ${locked ? "dp-eye-locked" : ""}`}>
            <circle cx={CX} cy={CY} r="30" className="dp-eye-outer" />
            <circle cx={CX} cy={CY} r="21" className="dp-eye-inner" />
            <circle cx={CX} cy={CY} r="14" className="dp-eye-core" fill="url(#dp-eye-core)" />
            {/* iris radial lines */}
            <g className="dp-eye-iris">
                {spokes.map((s, i) => (
                    <line key={i} x1={CX} y1={CY} x2={s.x2} y2={s.y2} />
                ))}
            </g>
            <circle cx={CX} cy={CY} r="3.5" className="dp-eye-pupil" />
        </g>
    );
}

/* ─── Lock render (gaze line + crosshair + detail panel) ─── */
function LockRender({ lock }) {
    const showLine  = ["line", "hold", "release"].includes(lock.phase);
    const showPanel = ["hold", "release"].includes(lock.phase);
    const p0 = lock.points[0];
    const p1 = lock.points[1] || null;
    const isCross = lock.cross && !!p1;
    const sev = p0.sev;

    // Panel position: push toward canvas edge
    const angle = Math.atan2(p0.y - CY, p0.x - CX);
    const pushDist = 60;
    const pPanelAnchor = p1 || p0;
    const aAnchor = Math.atan2(pPanelAnchor.y - CY, pPanelAnchor.x - CX);
    let panelX = pPanelAnchor.x + pushDist * Math.cos(aAnchor);
    let panelY = pPanelAnchor.y + pushDist * Math.sin(aAnchor);
    const pw = isCross ? 240 : 190;
    const ph = isCross ? 52 : 46;
    // keep in bounds
    panelX = Math.max(12, Math.min(W - pw - 12, panelX - pw / 2));
    panelY = Math.max(40, Math.min(H - ph - 30, panelY - ph / 2));

    return (
        <g className={`dp-lock dp-lock-${sev} ${isCross ? "dp-lock-cross" : ""} dp-lock-${lock.phase}`}>
            {/* gaze line */}
            {showLine && (
                isCross ? (
                    <g>
                        <line x1={CX} y1={CY} x2={p0.x} y2={p0.y} className="dp-gaze" />
                        <line x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} className="dp-gaze dp-gaze-cross" />
                        {/* orange spark on connection segment midpoint */}
                        <circle cx={(p0.x + p1.x) / 2} cy={(p0.y + p1.y) / 2} r="3" className="dp-cross-spark" />
                    </g>
                ) : (
                    <line x1={CX} y1={CY} x2={p0.x} y2={p0.y} className="dp-gaze" />
                )
            )}

            {/* crosshair on target(s) */}
            {showLine && <Crosshair x={p0.x} y={p0.y} sev={sev} />}
            {showLine && isCross && <Crosshair x={p1.x} y={p1.y} sev={p1.sev} />}

            {/* detail panel */}
            {showPanel && (
                <g transform={`translate(${panelX}, ${panelY})`}>
                    <g className="dp-panel">
                        <rect x="0" y="0" width={pw} height={ph} rx="5" className="dp-panel-bg" />
                        <text x="10" y="14" className={`dp-panel-head ${isCross ? "dp-panel-head-cross" : ""}`}>
                            {isCross
                                ? "CROSS-DIMENSIONAL · CORRELATION DETECTED"
                                : (sev === "critical" ? `${p0.stratumId.toUpperCase()} · CRITICAL` : `${p0.stratumId.toUpperCase()} · ATTENTION`)}
                        </text>
                        <text x="10" y={isCross ? 30 : 29} className="dp-panel-body">
                            {lock.finding?.finding || ""}
                        </text>
                        <text x="10" y={isCross ? 47 : 44} className="dp-panel-sub">
                            <tspan className="dp-panel-link-spark">⚡</tspan>
                            <tspan dx="3">{lock.finding?.link || lock.finding?.sub || ""}</tspan>
                        </text>
                    </g>
                </g>
            )}
        </g>
    );
}

function Crosshair({ x, y, sev }) {
    return (
        <g className={`dp-xhair dp-xhair-${sev}`}>
            <circle cx={x} cy={y} r="9" className="dp-xhair-ring" />
            <line x1={x - 11} y1={y} x2={x - 5} y2={y} className="dp-xhair-line" />
            <line x1={x + 5}  y1={y} x2={x + 11} y2={y} className="dp-xhair-line" />
            <line x1={x} y1={y - 11} x2={x} y2={y - 5}  className="dp-xhair-line" />
            <line x1={x} y1={y + 5}  x2={x} y2={y + 11} className="dp-xhair-line" />
        </g>
    );
}
